import React from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { LogOut, FileText, Image as ImageIcon, KeyRound, Loader2, Inbox, Briefcase, Users as UsersIcon } from "lucide-react";

const NavItem = ({ to, icon: Icon, children, testid }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 text-sm tracking-wide rounded-md transition-colors ${
        isActive
          ? "bg-zinc-900 text-white"
          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
      }`
    }
  >
    <Icon size={16} strokeWidth={1.5} />
    <span>{children}</span>
  </NavLink>
);

const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="animate-spin text-zinc-400" size={28} />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 font-sans" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 w-64 bg-white border-r border-zinc-200 flex flex-col">
        <div className="px-6 py-6 border-b border-zinc-100">
          <p className="text-[10px] tracking-[0.3em] text-zinc-400 uppercase">Millpond CMS</p>
          <p className="text-base font-medium text-zinc-900 mt-1">Admin Console</p>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          <NavItem to="/admin/pages" icon={FileText} testid="nav-pages">Pages</NavItem>
          <NavItem to="/admin/media" icon={ImageIcon} testid="nav-media">Media Library</NavItem>
          <NavItem to="/admin/leads" icon={Inbox} testid="nav-leads">Inquiries</NavItem>
          <NavItem to="/admin/applications" icon={Briefcase} testid="nav-applications">Applications</NavItem>
          <NavItem to="/admin/users" icon={UsersIcon} testid="nav-users">Users</NavItem>
          <NavItem to="/admin/account" icon={KeyRound} testid="nav-account">Account</NavItem>
        </nav>

        <div className="px-4 py-4 border-t border-zinc-100">
          <div className="text-xs text-zinc-500 mb-3">Signed in as <span className="text-zinc-900 font-medium">{user.username}</span></div>
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="flex items-center gap-2 text-xs text-zinc-600 hover:text-zinc-900 transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="pl-64">
        <div className="max-w-5xl mx-auto px-10 py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
